import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ password: "", confirm: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.password || !formData.confirm) {
      toast.error("Please fill in all fields");
      return;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (formData.password !== formData.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: formData.password,
      });
      toast.success("Password reset! Please sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not reset password. The link may be invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img
              src="/protocall-logo.png"
              alt="ProToCall"
              className="h-16 w-auto"
            />
          </Link>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Set a New Password
          </h1>
          <p className="text-slate-600 mt-2">Choose a strong password you'll remember</p>
        </div>

        <Card className="bg-white border border-slate-200 shadow-lg">
          <CardContent className="pt-6">
            {!token ? (
              <div className="text-center py-4" data-testid="reset-no-token">
                <p className="text-slate-700">
                  This reset link is invalid or incomplete. Please request a new one.
                </p>
                <Button asChild className="mt-6 bg-slate-900 hover:bg-slate-800 text-white" data-testid="request-new-link-btn">
                  <Link to="/forgot-password">Request New Link</Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="password" className="text-slate-700">
                    New Password
                  </Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      className="pl-10 h-11"
                      data-testid="reset-password-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm" className="text-slate-700">
                    Confirm Password
                  </Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="confirm"
                      type="password"
                      value={formData.confirm}
                      onChange={(e) => setFormData({ ...formData, confirm: e.target.value })}
                      placeholder="••••••••"
                      className="pl-10 h-11"
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold h-11"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
            )}

            <div className="mt-6 text-center text-sm">
              <Link to="/login" className="text-orange-500 hover:text-orange-600 font-medium" data-testid="login-link">
                ← Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
